import { useTranslation } from "react-i18next";
import { FC, useMemo } from "react";
import { MRT_ColumnDef, MaterialReactTable } from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { Link } from "react-router-dom";
import styles from "@/components/subjectOverview/subjectOverviewManageTab.module.css";
import MrtToolbarInternalActions from "@/components/tables/mrtToolbarInternalActions";
import {
  hasMrtFiltersToReset,
  resetMrtFilters,
} from "@/components/tables/mrtTableReset.utils";

interface SubjectOverviewManageProps {
  detailedSubject: any;
}

export const SubjectOverviewManageTab: FC<SubjectOverviewManageProps> = ({
  detailedSubject,
}) => {
  const { t } = useTranslation();
  const logedInUserInfo: any = useSelector((state: RootState) => state.login);

  const data = useMemo<any[]>(
    () => detailedSubject?.company_management_set ?? [],
    [detailedSubject],
  );

  const renderCompanyItem = (row: any) => {
    let result = row?.company_subject?.name || "";

    result = (
      <Link
        className={styles.link}
        to={`/subjectOverview/${row?.company_subject?.id}`}
        target="_blank"
      >
        {row?.company_subject?.name}
      </Link>
    );

    return <>{result}</>;
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorFn: (row: any) => {
          return <>{renderCompanyItem(row)}</>;
        },
        header: t("company"),
        id: "company",
      },
      {
        accessorFn: (row: any) =>
          row?.manager_type ? t(row.manager_type) : "",
        header: t("type"),
        id: "manager_type",
      },
      {
        accessorFn: (row: any) =>
          (row?.managed_department_set ?? [])
            .map((department: any) => department?.name)
            .filter(Boolean)
            .join(", "),
        header: t("departments"),
        id: "departments",
      },
    ],
    [t],
  );

  return (
    <MaterialReactTable
      localization={
        logedInUserInfo.preferred_language === "en"
          ? MRT_Localization_EN
          : MRT_Localization_DE
      }
      columns={columns}
      data={data}
      getRowId={(row: any) => row.id}
      enableFullScreenToggle={false}
      enableColumnActions={false}
      enableColumnFilters={false}
      enableHiding={false}
      enableDensityToggle={false}
      autoResetPageIndex={false}
      positionToolbarAlertBanner="none"
      muiTablePaperProps={{
        sx: {
          width: "100%",
          marginTop: "1rem",
          "& .MuiAlert-root": { backgroundColor: "#fff" },
          "& .MuiInputBase-root, && .MuiInput-root::before, && .MuiInput-root::after":
            { borderBottom: "none" },
          "& .MuiTextField-root": {
            backgroundColor: "#f4f2f2",
            outline: "none",
          },
        },
      }}
      muiTableBodyRowProps={({ row }: any) =>
        row.index % 2 === 0
          ? { sx: { backgroundColor: "#f5f5f5" } }
          : { sx: { backgroundColor: "#FFF" } }
      }
      muiTableBodyCellProps={{
        sx: {
          borderRight: "1px solid #e0e0e0",
        },
      }}
      initialState={{
        density: "compact",
        showGlobalFilter: true,
      }}
      positionGlobalFilter="left"
      enableRowSelection={false}
      enableRowActions={false}
      renderToolbarInternalActions={({ table }) => (
        <MrtToolbarInternalActions
          table={table}
          showReset={hasMrtFiltersToReset(table.getState())}
          onReset={() => resetMrtFilters(table)}
        />
      )}
    />
  );
};

export default SubjectOverviewManageTab;
